import { type UserMetadata } from '@choco-admin/types';
import React, { useState, useCallback } from 'react';

import {
  EntitySearchModal,
  EntitySearchOption,
} from '../../../../../../../components/FormComponents';
import { useNotification } from '../../../../../../../contexts/NotificationContext';
import { useChatUserOperations } from '../../../../../hooks';
import { useSearchBuyerUsers } from '../../../../../hooks/useSearchBuyerUsersForChat';
import { useChatPageContext } from '../../../contexts';

interface AddBuyerUserModalProps {
  isOpen: boolean;
  onClose: () => void;
  chatId: string;
  buyerId: string;
}

export const AddBuyerUserModal: React.FC<AddBuyerUserModalProps> = ({
  isOpen,
  onClose,
  chatId,
  buyerId,
}) => {
  const [error, setError] = useState<string | null>(null);
  const [isAdding, setIsAdding] = useState(false);
  const { updateChatData } = useChatPageContext();
  const { addNotification } = useNotification();

  const { addUsers } = useChatUserOperations({
    onSuccess: () => {
      addNotification({
        title: 'User Added',
        message: 'User has been successfully added to the chat.',
        type: 'success',
      });
      onClose();
    },
    onError: (addError) => {
      addNotification({
        title: 'Add Failed',
        message: addError.message || 'Failed to add user to chat.',
        type: 'error',
      });
    },
  });

  const {
    users,
    isLoading: searchLoading,
    searchUsers,
  } = useSearchBuyerUsers({
    buyerId,
  });

  const handleSearch = useCallback(
    (searchTerm: string) => {
      if (searchTerm.trim()) {
        searchUsers(searchTerm.trim());
      }
    },
    [searchUsers]
  );

  const handleConfirm = async (selectedEntity: EntitySearchOption) => {
    setError(null);
    setIsAdding(true);

    try {
      await addUsers([
        {
          chatId,
          userId: selectedEntity.id,
          userType: 'buyer',
          operation: 'add',
        },
      ]);

      // Update local state to add the user immediately
      updateChatData((prevData) => {
        if (!prevData) return prevData;
        return {
          ...prevData,
          users: [
            ...prevData.users,
            {
              id: selectedEntity.id,
              name: selectedEntity.name,
              email: selectedEntity.metadata?.email,
              phone: selectedEntity.metadata?.phone,
              supplier: false,
              orderPreferences: {
                whatsApp: selectedEntity.metadata?.whatsApp || false,
                sms: selectedEntity.metadata?.sms || false,
                fax: selectedEntity.metadata?.fax || false,
              },
            },
          ],
        };
      });
    } catch (confirmError) {
      // Error is handled by the hook's onError callback
      setIsAdding(false);
    }
  };

  const handleErrorDismiss = () => {
    setError(null);
  };

  return (
    <EntitySearchModal
      isOpen={isOpen}
      onClose={onClose}
      title="Add Buyer User"
      searchPlaceholder="Search buyer users by name or email..."
      entityType="buyer user"
      isSearching={searchLoading}
      emptyMessage={
        users.length === 0 && !searchLoading
          ? 'The selected buyer has no buyer users'
          : undefined
      }
      searchResults={users.map((user) => ({
        id: user.value,
        name: user.label,
        label: user.label,
        subtitle: (user.metaData as UserMetadata)?.email || '',
        metadata: {
          email: (user.metaData as UserMetadata)?.email || '',
          phone: (user.metaData as UserMetadata)?.phone || '',
          whatsApp:
            (user.metaData as UserMetadata)?.orderPreferences?.some(
              (pref: { type: string }) => pref.type === 'whatsapp'
            ) || false,
          sms:
            (user.metaData as UserMetadata)?.orderPreferences?.some(
              (pref: { type: string }) => pref.type === 'sms'
            ) || false,
          fax:
            (user.metaData as UserMetadata)?.orderPreferences?.some(
              (pref: { type: string }) => pref.type === 'fax'
            ) || false,
        },
      }))}
      onSearch={handleSearch}
      onConfirm={handleConfirm}
      isConfirming={isAdding}
      error={error}
      onErrorDismiss={handleErrorDismiss}
    />
  );
};
